import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { X, Camera, SwitchCamera, Keyboard } from 'lucide-react';

const BARCODE_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.QR_CODE,
];

const BarcodeScanner = ({ onScan, onClose }) => {
  const html5QrCodeRef = useRef(null);
  const [error, setError] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanCount, setScanCount] = useState(0);
  const scannerIdRef = useRef(`barcode-reader-${Date.now()}`);
  const cameraIdRef = useRef(null);

  const startScanner = async () => {
    try {
      // Wait for DOM element
      await new Promise(r => setTimeout(r, 300));

      const el = document.getElementById(scannerIdRef.current);
      if (!el) { setError('Error interno del escáner'); return; }

      const scanner = new Html5Qrcode(scannerIdRef.current, {
        formatsToSupport: BARCODE_FORMATS,
        verbose: false
      });
      html5QrCodeRef.current = scanner;

      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) {
        setError('No se detectaron cámaras. Intenta escribir el código manualmente.');
        setShowManual(true);
        return;
      }

      // Prefer back camera
      const backCamera = cameras.find(c => c.label.toLowerCase().includes('back') || c.label.toLowerCase().includes('rear') || c.label.toLowerCase().includes('trasera'));
      const cameraId = backCamera ? backCamera.id : cameras[cameras.length - 1];
      cameraIdRef.current = cameraId;

      await scanner.start(
        typeof cameraId === 'string' ? cameraId : cameraId.id,
        {
          fps: 15,
          qrbox: { width: 280, height: 150 },
        },
        (decodedText) => {
          // Pause, notify, then resume scanning
          scanner.pause(true);
          setScanCount(c => c + 1);
          onScan(decodedText.trim());
          // Resume after short delay to avoid duplicate reads
          setTimeout(() => {
            try { scanner.resume(); } catch {}
          }, 1200);
        },
        () => {}
      );
      setScanning(true);
      setError('');
    } catch (err) {
      console.error('Scanner error:', err);
      setError('No se pudo acceder a la cámara. Usa la opción manual.');
      setShowManual(true);
    }
  };

  useEffect(() => {
    startScanner();
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
        html5QrCodeRef.current = null;
      }
    };
  }, []);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
        html5QrCodeRef.current = null;
      }
      onScan(manualCode.trim());
    }
  };

  const handleClose = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(() => {});
      html5QrCodeRef.current = null;
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center" data-testid="barcode-scanner-overlay">
      <div className="w-full max-w-md px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-lg flex items-center gap-2">
            <Camera size={22} />
            Escanear Código
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => setShowManual(!showManual)}
              className="p-2 bg-white/20 rounded-lg text-white hover:bg-white/30"
              data-testid="manual-code-toggle"
              title="Ingresar código manual"
            >
              <Keyboard size={20} />
            </button>
            <button onClick={handleClose} className="p-2 bg-white/20 rounded-lg text-white hover:bg-white/30" data-testid="close-scanner-btn">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Camera View */}
        <div className="rounded-xl overflow-hidden bg-black relative" style={{ minHeight: '260px' }}>
          <div id={scannerIdRef.current} style={{ width: '100%' }} />
          {!scanning && !error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-white/50 text-sm">Iniciando cámara...</div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <p className="text-amber-400 text-sm text-center mt-3 bg-amber-400/10 rounded-lg p-2">{error}</p>
        )}

        {/* Manual Input */}
        {showManual && (
          <form onSubmit={handleManualSubmit} className="mt-4 flex gap-2">
            <input
              type="text"
              value={manualCode}
              onChange={e => setManualCode(e.target.value)}
              placeholder="Escribe el código de barras"
              className="flex-1 px-4 py-3 rounded-xl bg-white/10 text-white border border-white/20 text-sm focus:outline-none focus:border-white/50"
              autoFocus
              data-testid="manual-barcode-input"
            />
            <button
              type="submit"
              disabled={!manualCode.trim()}
              className="px-5 py-3 rounded-xl bg-green-500 text-white font-bold text-sm disabled:opacity-40"
              data-testid="manual-barcode-submit"
            >
              Buscar
            </button>
          </form>
        )}

        <p className="text-white/40 text-xs text-center mt-3">
          {scanCount > 0
            ? `${scanCount} producto(s) escaneado(s) - Sigue escaneando`
            : 'Apunta la cámara al código de barras o usa el teclado'}
        </p>
      </div>
    </div>
  );
};

export default BarcodeScanner;
